<script lang="ts" setup>
    import {RoleEnum} from "~/types/User";
    import type {PortfolioType} from "~/types/Portfolio";
    import { Button } from '~/components/ui/button'
    import UserAvatar from '~/components/custom-ui/UserAvatar.vue'
    import RatingStars from '~/components/custom-ui/RatingStars.vue'
    import SuggestOrder from '~/components/pages/catalog/SuggestOrder.vue'
    import TagBadge from '~/components/custom-ui/TagBadge.vue'
    import PhotoViewer from '~/components/custom-ui/PhotoViewer.vue'

    const route = useRoute();
    
    const userStore = useUserStore();
    const globalStore = useGlobalStore()

    const props = defineProps({
        item: {
            type: Object as PropType<PortfolioType>,
            required: true
        },
        current_specification: {
            type: String,
            required: true,
        },
        parent_specification: {
            type: String,
        }
    })

    const portfolio_photo = props.item?.photos?.[0]?.photo || null

    const masterSpecificationIds = computed(() => {
        return props.item.specifications.map(item => item.documentId)
    })

    const specificationTags = computed(() => {
        return globalStore.specifications.filter(item => {
            return props.item.specifications.map(spec => spec.documentId).includes(item.documentId)
        }).filter(item => !!item.parent_id)
    })

    const isParentSpecification = computed(() => props.current_specification == props.parent_specification)
</script>

<template>
    <div class="border-gray-200 border flex-row flex-wrap md:flex-nowrap rounded-2xl flex items-start justify-start p-5 lg:p-6">
        <div class="flex lg:flex-col flex-row w-full lg:w-auto">
            <UserAvatar :src="item.user.avatar?.formats?.small?.url || item.user?.avatar_url" :name="item.user.username" size="xl" shape="square" class="lg:w-full cstm_style_avatar mr-4 lg:mr-0" />

           <div class="w-full">
            <h2 class="text-[16px] font-medium block lg:hidden">{{ item.user.username }}</h2>
            <RatingStars v-if="item.user.compute_rating" :rating="item.user.compute_rating" class="mt-0 lg:mt-2 lg:justify-center" />

            <div v-if="item.user.compute_review_count" class="flex w-full items-center lg:justify-center mt-0 lg:mt-2">
                <Icon class="text-gray-600" size="24" name="ri:chat-heart-line" />
                <p class="font-medium text-sm m-1"> {{ item.user.compute_review_count }} {{ $t("reviews") }}</p>
            </div>
           </div>
        </div>


        <div class="flex-grow w-full lg:w-auto flex flex-col items-start mt-4 lg:mt-0 justify-between lg:h-full lg:ltr:ml-6 rtl:mr-6">
            <div class="top_row ">
                <h2 class="text-lg font-medium hidden lg:block">{{ item.user.username }}</h2>

<!--                <div v-if="item.tags?.length" class="flex flex-wrap gap-2 mt-2 w-fit">
                    <CustomUiTagBadge v-for="tag in item.tags">{{tag.name}}</CustomUiTagBadge>
                </div>-->

                <div v-if="item.specifications?.length" class="flex flex-wrap gap-2 mt-2 w-fit">
                    <TagBadge v-for="tag in specificationTags" variant="specification">{{tag.name}}</TagBadge>
                </div>

                <p class="text-sm mt-2 line-clamp-4 max-w-full [overflow-wrap:anywhere]">
                    {{ item.description }}
                </p>
            </div>

            <div class="mt-3 grid grid-cols-2 w-full md:w-fit md:grid-flow-col gap-4">
                <SuggestOrder
                    v-if="userStore.isAuthenticated && userStore.user?.user_role != RoleEnum.MASTER"
                    :master_id="item.documentId"
                    :specification_id="route.params.id" 
                    :onlySpecificationIds="isParentSpecification ? masterSpecificationIds : [current_specification]"
                />

                <Button as-child variant="outline" class="border-primary-500 rounded-lg px-3 py-2 text-sm font-medium text-primary-500 hover:bg-primary-50 hover:text-primary-500 hover:border-transparent">
                    <NuxtLink :to="{name: 'portfolio', params: {id: item.documentId}}">{{ $t("Portfolio") }}</NuxtLink>
                </Button>
            </div>
        </div>

        <div v-if="portfolio_photo" class="flex items-center h-full shrink-0">
            <PhotoViewer :url="portfolio_photo" :thumbnail="portfolio_photo" />
        </div>
    </div>
</template>
